import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface AnimatedStatCardProps {
  title: string;
  value: number;
  icon: LucideIcon;
  gradient: string;
  iconBg: string;
  trend?: number;
  onClick?: () => void;
}

export const AnimatedStatCard = ({ 
  title, 
  value, 
  icon: Icon, 
  gradient, 
  iconBg,
  trend,
  onClick 
}: AnimatedStatCardProps) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 1000;
    const increment = value / (duration / 16);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <Card 
      className={cn(
        "p-3 md:p-4 bg-gradient-to-br border transition-all duration-300 hover:scale-105 hover:shadow-lg animate-fade-in",
        gradient,
        onClick && "cursor-pointer"
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-2 md:gap-3 mb-2">
        <div className={cn(
          "w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center flex-shrink-0 animate-pulse",
          iconBg
        )}>
          <Icon className="w-4 h-4 md:w-5 md:h-5 text-white" />
        </div>
        {trend !== undefined && (
          <div className={cn(
            "text-xs font-medium px-2 py-0.5 rounded-full",
            trend >= 0 ? "bg-green-500/20 text-green-600" : "bg-red-500/20 text-red-600"
          )}>
            {trend >= 0 ? "+" : ""}{trend}%
          </div>
        )}
      </div>
      <div className="text-[10px] md:text-xs text-muted-foreground uppercase tracking-wider">
        {title}
      </div>
      <div className="text-2xl md:text-3xl font-bold mt-1">
        {displayValue}
      </div>
    </Card>
  );
};
